import { Center, Loader } from "@mantine/core";

import { safeApiCall } from "api/common.service";
import { getDebtorSummary } from "api/debtor.service";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DebtorSummaries } from "types/model.types";

const CustomerListPreview: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<DebtorSummaries | null>();
  const [params] = useSearchParams();
  const searchTerm = params.get("s");

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const result = await safeApiCall(getDebtorSummary());
        if (mounted && result) setData(result);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();
    if (!data) return [];

    return data.filter((b) => {
      const name = b.name?.toLowerCase() ?? "";

      const matchesSearch = [
        b.name,
        b.primaryAddress,
        b.attn1,
        b.attn2,
        b.email,
      ].some((field) => {
        return (field ?? "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .includes(normalizedSearch);
      });

      return matchesSearch;
    });
  }, [data, searchTerm]);

  const rows = filteredItems?.map((debtor) => {
    return (
      <tr key={debtor.id}>
        <td>{debtor.id}</td>
        <td
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {debtor.name}
        </td>
        <td
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {(debtor.primaryAddress ?? "").split(/\r?\n/).map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </td>
        <td
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {debtor.phone}
        </td>
        <td
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <div>
            <span>Attn1: </span>
            {debtor.attn1}
          </div>
          <div>
            <span>Attn2: </span>
            {debtor.attn2}
          </div>
        </td>
        <td
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {debtor.email}
        </td>
        <td>{debtor.active ? "Y" : "N"}</td>
        <td></td>
      </tr>
    );
  });

  if (loading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <div className="sub-title">Debtor Accounts</div>
      <table className="details-table">
        <colgroup>
          <col className="word-break" style={{ width: "10%" }} />
          <col className="word-break" style={{ width: "15%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "15%" }} /> {/* description */}
          <col
            style={{
              width: "30%",
            }}
          />
          <col className="word-break" style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>id</th>
            <th>Name</th>
            <th>Delivery Address</th>
            <th>Phone</th>
            <th
              style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Attention
            </th>
            <th>Email</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </>
  );
};

export default CustomerListPreview;
